import { appendCalendarScope, calendarScopeSearch, type CalendarScopeQuery } from '@/lib/calendarScope';
import type { CategoryRuleTarget } from '@/types/category-budget';
import type {
  ConsentState,
  DeclaredExternalAccount,
  OnboardingState,
  SemanticClassUpdateResult,
  SemanticClassWire,
  StreamCandidatesResponse,
  StreamConfirmationRequest,
  StreamConfirmationResponse,
  UpdateOnboardingStateRequest,
} from '@/types/onboarding';
import type { BillingAddress, SetBillingAddressRequest } from '@/types/billing';
import type { NotificationTokenResponse } from '@/types/notifications';
import type { AnswerSpaceInviteRequest, CreateSpaceRequest, MySpaceInvite, MySpaceSendRules, MySpaceRulesOverview, SpaceSendRule, SendToSpaceRequest, SendToSpaceResult, SpaceBatchPreview, SentExpense, SpaceDetail, SpaceInvite, SpaceAuditHistory, SpaceBudget, SetSpaceBudgetRequest, SpaceCommitments, SpaceInviteRequest, SpaceRole, SpaceSummary } from '@/types/spaces';
import type {
  Account,
  AccountWithLimits,
  Transaction,
  TransactionTag,
  TransactionDetails,
  UpdateTransactionDto,
  InvestmentItem,
  InvestmentSummary,
  InvestmentDetail,
  InvestmentDuplicateCandidate,
  InvestmentSourceRole,
  InstitutionFacet,
  HomeSummary,
  SpendingPace,
  OpenInvoicesWidget,
  NetWorthTrendWidget,
  AvailableToSpend,
  GoalsOverview,
  GoalDetail,
  GoalContributionHistoryEntry,
  LinkableEntity,
  CreateGoalDto,
  UpdateGoalDto,
  CreateProblemReportRequest,
  ParentCategory,
  CategoryOverviewResponse,
  UpsertCategoryLimitRequest,
  UpsertCategoryGoalLinkRequest,
  CreateCustomCategoryRequest,
  UpdateCustomCategoryRequest,
  CustomCategorySummary,
  SuggestCategoryRequest,
  CategorySuggestion,
  SuggestSimilarCategoryRequest,
  SimilarCategorySuggestion,
  CategoryRule,
  ReviewQueueResponse,
  ConfirmCategoryRequest,
  ConfirmCategoryResult,
  Page,
} from '@/types';

import { API_URL } from '@/lib/env';
import type { RegisterSpaceSettlement, SpaceLedger, SpaceSettlementHistory, SpaceSettlementReceipt, SpaceExpensesOverview } from '@/types/space-settlements';

interface ApiOptions extends RequestInit {
  token?: string;
}

function isJsonContentType(contentType: string): boolean {
  return contentType.toLowerCase().includes('json');
}

export type LimitExceededDetail = {
  code: 'limit_exceeded';
  limit: string;
  current: number;
  max: number;
  upgradeUrl: string;
  message?: string;
};

class ApiError extends Error {
  /** Structured body returned with the error (e.g. limit_exceeded details on 402). */
  payload?: LimitExceededDetail | Record<string, unknown>;

  constructor(public status: number, message: string, payload?: LimitExceededDetail | Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.payload = payload;
  }

  /**
   * Check if this is an authentication-related error
   * that might be resolved by refreshing the token
   */
  isAuthError(): boolean {
    return (
      this.status === 401 ||
      this.message.toLowerCase().includes('token') ||
      this.message.toLowerCase().includes('unauthorized') ||
      this.message.toLowerCase().includes('authenticated')
    );
  }

  /** True when the backend rejected an action because the user's plan limits were hit. */
  isLimitExceeded(): this is ApiError & { payload: LimitExceededDetail } {
    return this.status === 402
      && typeof this.payload === 'object'
      && this.payload !== null
      && 'code' in this.payload
      && (this.payload as LimitExceededDetail).code === 'limit_exceeded';
  }
}

function isLimitExceededDetail(payload: unknown): payload is LimitExceededDetail {
  if (typeof payload !== 'object' || payload === null) return false;
  const detail = payload as Partial<LimitExceededDetail>;
  return detail.code === 'limit_exceeded'
    && typeof detail.limit === 'string'
    && Number.isFinite(detail.current)
    && Number.isFinite(detail.max)
    && typeof detail.upgradeUrl === 'string';
}

const LIMIT_LABELS: Record<string, string> = {
  bank_connections: 'conexões bancárias',
  goals: 'metas',
  active_budgets: 'orçamentos ativos',
  ai_messages: 'mensagens de IA',
};

function buildLimitExceededMessage(detail: LimitExceededDetail): string {
  if (typeof detail.message === 'string' && detail.message.trim()) return detail.message;
  const label = LIMIT_LABELS[detail.limit] ?? detail.limit;
  return `Seu plano atual permite até ${detail.max} ${label}. Faça upgrade para continuar.`;
}

function readApiErrorMessage(error: unknown, fallback = 'Request failed'): string {
  if (isLimitExceededDetail(error)) return buildLimitExceededMessage(error);
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  return fallback;
}

/**
 * Reads a non-2xx Response into an ApiError with the same message/payload semantics as
 * request() below. The SSE chat path can't go through request() (it needs the raw body
 * stream on success) but must produce the identical error shape, so 402 limit payloads
 * work with isLimitExceeded()/handleApiError() there too.
 */
export async function apiErrorFromResponse(response: Response): Promise<ApiError> {
  const contentType = response.headers.get('content-type') ?? '';
  const isJson = isJsonContentType(contentType);
  const error = isJson
    ? await response.json().catch(() => ({ message: 'Request failed' }))
    : { message: `Request failed: ${response.status} ${response.statusText || ''}`.trim() };
  return new ApiError(response.status, readApiErrorMessage(error), isJson ? error : undefined);
}

/**
 * One id as one path segment. Encoding keeps `/` and `?` inside it, but no encoding neutralizes a dot segment
 * (URL parsers resolve `..` and `%2e%2e` alike), so an empty or dot-only id, which names no resource, is refused
 * before any request is made.
 */
export function pathSegment(id: string): string {
  if (id === '' || id === '.' || id === '..') throw new Error('Identificador inválido.');
  return encodeURIComponent(id);
}

async function request<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { token, ...fetchOptions } = options;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const url = `${API_URL}${endpoint}`;
  const response = await fetch(url, {
    cache: token && fetchOptions.cache === undefined ? 'no-store' : fetchOptions.cache,
    ...fetchOptions,
    headers,
  });

  const contentType = response.headers.get('content-type') ?? '';
  const isJson = isJsonContentType(contentType);

  if (!response.ok) {
    const error = isJson
      ? await response.json().catch(() => ({ message: 'Request failed' }))
      : { message: `Request failed: ${response.status} ${response.statusText || ''}`.trim() };
    throw new ApiError(response.status, readApiErrorMessage(error), isJson ? error : undefined);
  }

  const text = await response.text();
  if (!text || !text.trim()) return undefined as T;
  if (!isJson) {
    const preview = text.trim().slice(0, 80).replace(/\s+/g, ' ');
    throw new ApiError(
      response.status,
      `Expected JSON from ${url}, but received ${contentType || 'unknown content type'}: ${preview}`,
    );
  }
  return JSON.parse(text) as T;
}

export type ItemWithAccounts = {
  id: string;
  pluggyItemId: string;
  connectorId: number;
  connectorName: string;
  connectorImage: string | null;
  connectorPrimaryColor?: string | null;
  status?: string;
  executionStatus?: string;
  updatedAt?: string;
  lastUpdatedAt?: string;
  nextAutoSyncAt?: string;
  consentExpiresAt?: string;
  consentSignedAt?: string;
  consecutiveFailedLoginAttempts?: number;
  errorCode?: string;
  errorMessage?: string;
  products: string[];
  accounts: Account[];
  connectorHealth?: ConnectorHealth | null;
  connectionIssue?: ConnectionIssue | null;
};

export type ConnectorHealthStatus = 'ONLINE' | 'UNSTABLE' | 'OFFLINE' | 'UNKNOWN' | string;

export type ConnectorHealth = {
  connectorId: number;
  name: string;
  imageUrl: string | null;
  primaryColor: string | null;
  status: ConnectorHealthStatus;
  selectable: boolean;
  reason: string | null;
  updatedAt: string | null;
};

export type ConnectionIssue = {
  level: 'healthy' | 'attention' | 'critical' | string;
  kind: string;
  title: string;
  message: string;
  blockedActions: string[];
};

/** A page of GET /api/transactions: `total` counts every match, `nextOffset` is null on the last page. */
export type TransactionsResponse = Page<Transaction>;

// The largest page core.api answers: the grouping picker reads its candidates in as few requests as it can.
const GROUPING_CANDIDATES_PAGE = 200;

export type PagedResponse<T> = {
  results: T[];
  total: number;
  limit?: number;
  offset?: number;
  hasMore?: boolean;
  nextOffset?: number | null;
};

export type TransactionDailySummary = {
  date: string;
  income: number;
  expense: number;
};

export type TransactionSummaryMeta = {
  count: number;
  income: number;
  expense: number;
  net: number;
  incomeCount: number;
  expenseCount: number;
  dailySeries: TransactionDailySummary[];
};

export type TransactionCategoryMeta = {
  id: string;
  label: string;
  iconName: string | null;
  parentCategoryId: string;
  parentLabel: string;
  parentIconName: string | null;
};

export type TransactionCategoryFacet = TransactionCategoryMeta & {
  count: number;
  amount: number;
  share: number;
};

export type TransactionAccountFacet = {
  id: string;
  count: number;
};

export type TransactionPaymentFacet = {
  id: string;
  count: number;
};

/** One purchase channel present in the period (`transactions.platform` vocabulary).
 *  Null-platform rows are never a facet — null asserts no channel, not "bought directly". */
export type TransactionPlatformFacet = {
  id: string;
  count: number;
};

export type TransactionBreakdownMeta = {
  segments: Array<{ categoryId: string; total: number }>;
  total: number;
};

/** One tag present in the period (VER-378). Count is rows carrying the tag; amount is the
 *  absolute expense volume among them. Tags OVERLAP (a row carries many), so neither number
 *  may be summed across facets. Name/color resolve via the tags catalog. */
export type TransactionTagFacet = {
  id: string;
  count: number;
  amount: number;
};

export type TransactionsMetaResponse = {
  total: number;
  summary: TransactionSummaryMeta;
  categoryBreakdown: TransactionBreakdownMeta;
  categoryFacets: TransactionCategoryFacet[];
  accountFacets: TransactionAccountFacet[];
  paymentMethodFacets: TransactionPaymentFacet[];
  platformFacets: TransactionPlatformFacet[];
  tagFacets: TransactionTagFacet[];
  categories: TransactionCategoryMeta[];
};

function appendParamValues(params: URLSearchParams, key: string, value?: string | string[] | null) {
  if (!value) return;
  if (Array.isArray(value)) {
    value.filter(Boolean).forEach((entry) => params.append(key, entry));
    return;
  }
  params.append(key, value);
}

export type UserProfile = {
  id: string;
  email: string | null;
  name: string | null;
  hasCpf: boolean;
  cpfLast4: string | null;
  cpfMasked: string | null;
  hasWhatsAppUserId: boolean;
  whatsAppLast4: string | null;
  whatsAppMasked: string | null;
  whatsAppVerifiedAt: string | null;
  whatsAppVerificationRequired: boolean;
  whatsAppPendingVerification: WhatsAppVerificationStart | null;
  /** True once the user has ENTERED /onboarding (its state read creates the row);
   * users predating the flow stay false. Optional while the deployed API can
   * still serve the pre-field shape. */
  onboardingStarted?: boolean;
  onboardingCompletedAt?: string | null;
  /** The two-purpose consent state (VER-383). Optional while the deployed API can
   * still serve the pre-field shape. */
  consents?: ConsentState | null;
  /** The fiscal address (VER-386/389); rides here because Perfil always loads the
   * profile — it must not spread into any other ambient payload. Optional while
   * the deployed API can still serve the pre-field shape. */
  billingAddress?: BillingAddress | null;
  /** What the "Assinar" pre-flight reads — the boolean, never the address. */
  billingAddressComplete?: boolean;
};

/** PATCH /users/me — each field optional; absent leaves it as it is. */
export type UpdateProfileRequest = {
  cpf?: string;
  billingAddress?: SetBillingAddressRequest;
};

export type WhatsAppVerificationStart = {
  sent: boolean;
  maskedPhone: string;
  expiresAt: string;
  resendAvailableAt: string;
};

export const usersApi = {
  getMe: (token: string) => request<UserProfile>('/users/me', { token }),

  // The profile's writable fields, each optional: absent leaves a field as it is, and the
  // address is replaced whole. Every field sent is validated before anything is written.
  updateProfile: (token: string, data: UpdateProfileRequest) =>
    request<UserProfile>('/users/me', {
      method: 'PATCH',
      token,
      body: JSON.stringify(data),
    }),

  startWhatsAppVerification: (token: string, phoneNumber: string) =>
    request<WhatsAppVerificationStart>('/users/me/whatsapp/verification', {
      method: 'POST',
      token,
      body: JSON.stringify({ phoneNumber }),
    }),

  // Another code to the number already pending — the start route's `resend`, which takes no number.
  resendWhatsAppVerification: (token: string) =>
    request<WhatsAppVerificationStart>('/users/me/whatsapp/verification', {
      method: 'POST',
      token,
      body: JSON.stringify({ resend: true }),
    }),

  confirmWhatsAppVerification: (token: string, code: string) =>
    request<UserProfile>('/users/me/whatsapp/verification/confirm', {
      method: 'POST',
      token,
      body: JSON.stringify({ code }),
    }),

  // One idempotent PUT per purpose — the consent surface (VER-383).
  setConsent: (token: string, purpose: ConsentPurpose, data: SetConsentRequest) =>
    request<ConsentState>(`/users/me/consents/${purpose}`, {
      method: 'PUT',
      token,
      body: JSON.stringify(data),
    }),
};

export type ConsentPurpose = 'terms_privacy_openfinance' | 'whatsapp_messaging';

export type SetConsentRequest = {
  granted: boolean;
  version: string;
};

/**
 * Calls a route core.api moved, and its old path while the deployed API doesn't serve the new one
 * yet: a route the API doesn't have answers a bare 404 (or a 405 when only its method is new), where
 * a declared failure answers the problem envelope with its `code`. Goes with the old routes (VER-563).
 */
async function movedRoute<T>(current: () => Promise<T>, previous: () => Promise<T>): Promise<T> {
  try {
    return await current();
  } catch (error) {
    const missing = error instanceof ApiError
      && (error.status === 405 || (error.status === 404 && !(error.payload && 'code' in error.payload)));
    if (!missing) throw error;
    return previous();
  }
}

// Items API: the person's connections, at /connections, the API's name for them (VER-565, VER-579);
// the /items routes are being retired (VER-563).
export const itemsApi = {
  getConnectToken: (token: string, itemId?: string, redirectUri?: string) => {
    const body = JSON.stringify({ itemId, redirectUri });
    return movedRoute(
      () => request<{ accessToken: string; cpf: string }>('/connections/connect-token', { method: 'POST', token, body }),
      () => request<{ accessToken: string; cpf: string }>('/items/connect-token', { method: 'POST', token, body }),
    );
  },

  getAll: (token: string) => request<ItemWithAccounts[]>('/connections', { token }),

  // Answers nothing (204); until core.api's VER-579 is live, the old route's `{ success }`, unread.
  delete: (token: string, id: string) =>
    movedRoute(
      () => request<void>(`/connections/${pathSegment(id)}`, { method: 'DELETE', token }),
      () => request<void>(`/items/${pathSegment(id)}`, { method: 'DELETE', token }),
    ),
};

export const connectorsApi = {
  getHealth: (token: string) => request<ConnectorHealth[]>('/connectors/health', { token }),
};

// Accounts API
export const accountsApi = {
  getAll: (token: string) => request<AccountWithLimits[]>('/accounts', { token }),
};

// Expenses Summary types (matches API Design Guide)
export type ExpenseTransaction = {
  id: string;
  date: string;
  referenceDate?: string | null;
  description: string;
  value: number;
  method: 'debit' | 'credit';
  accountId: string;
  accountName: string;
  accountImage: string | null;
  isExpected: boolean;
  isParcela: boolean;
  parcelaInfo: {
    current: number;
    total: number;
    remainingValue: number;
  } | null;
  aiRefinement: string | null;
  category: string | null;
  categoryId?: string | null;
  categoryTranslated?: string | null;
  categoryIcon?: string | null;
  parentCategoryId?: string | null;
  parentCategory?: string | null;
  parentCategoryTranslated?: string | null;
  parentCategoryIcon?: string | null;
  isRefund?: boolean;
  // User-editable fields
  userCategory?: string | null;
  effectiveDate?: string | null;
  // Display label resolved by the backend from Pluggy category metadata.
  effectiveCategory: string;
  // The spaces this charge is shared in (VER-571), as a transaction row carries them; absent when none.
  spaceExpenses?: SentExpense[] | null;
};

// Billing cycle position type
export type BillingCyclePosition = 'closing' | 'opening';

// Credit Card Cockpit Types
export type CreditCardInvoice = {
  billId: string | null;
  isOpen: boolean;
  isPaid: boolean;
  position: BillingCyclePosition;
  period: string; // YYYY-MM format (month when cycle started)
  label: string; // Display label
  dueDate: string | null;
  closeDate: string | null;
  total: number;
  /** Open invoices: the posted charges half of `total` (charges the bank has actually recorded). */
  realTotal?: number | null;
  /** Open invoices: the projected-installments half of `total` (not yet charged by the bank). */
  projectedTotal?: number | null;
  transactionCount: number;
  /**
   * True when this open invoice is a placeholder: the just-closed bill has a total but its
   * transactions haven't been associated yet, so projection is withheld and the UI shows a
   * "waiting on the bank" (Sincronizando) state instead of figures.
   */
  awaitingBillSync?: boolean;
  /**
   * True when the platform synthesized this invoice from a detected bill payment because
   * the bank has not delivered the closed bill yet. Two shapes: the estimated CLOSED bill
   * (`isOpen=false`, `isPaid=true`, `total` = the payment; members may not sum to `total`)
   * and the estimated OPEN bill (`isOpen=true`, `cycleStart` = estimated close + 1 day).
   */
  isEstimated?: boolean;
  /** Estimated closed bill only: the detected payment amount. */
  paidAmount?: number | null;
  /** Estimated closed bill only: ISO date of the detected payment. */
  paidAt?: string | null;
  /** Estimated invoices (both shapes): ISO date of the estimated close. */
  estimatedCloseDate?: string | null;
  cycleStart?: string | null;
  cycleEnd?: string | null;
  cycleProgress?: number | null;
  topCategoryIds?: string[];
  /**
   * Closed invoices only: fingerprint of the canonical charges this payload shows.
   * The mark-as-paid endpoint requires it back so a confirmation is rejected (409)
   * when the cycle changed after the user viewed it. Null on open invoices.
   */
  chargeFingerprint?: string | null;
};

export type CreditCardCockpitAccount = {
  id: string;
  name: string;
  image: string | null;
  number: string | null;
  creditBrand: string | null;
  /** The APPROVED limit. Never subtract creditAvailableLimit from this — available is computed
   *  against creditEffectiveLimit, so the subtraction invents spend that never happened. */
  creditLimit: number | null;
  creditAvailableLimit: number | null;
  /** The limit actually in effect: the cardholder's own cap when they set one in the bank's app,
   *  otherwise creditLimit. The denominator for any usage figure. */
  creditEffectiveLimit: number | null;
  /** Committed amount, from the bank's own open-invoice balance. The numerator. */
  creditUsedAmount: number | null;
  /** False when the limit figures don't reconcile or the card is unlimited — show the available
   *  amount alone rather than a usage percentage. */
  creditLimitReliable: boolean;
  creditMinimumPayment: number | null;
  dueDay: number | null;
  connectorId: number | null;
  connectorPrimaryColor: string | null;
  colorOverride: string | null;
  invoices: CreditCardInvoice[];
  installmentLadder: InstallmentLadderEntry[] | null;
};

export type InstallmentLadderEntry = {
  month: string; // YYYY-MM format
  monthLabel: string; // e.g., "Fev 26"
  amount: number;
};

export type CreditCardCockpitResponse = {
  accounts: CreditCardCockpitAccount[];
  debitSummary: {
    total: number;
    transactionCount: number;
  };
  installmentLadder: InstallmentLadderEntry[] | null;
};

export type CategoryBreakdownItem = {
  category: string;
  categoryId?: string | null;
  parentCategoryId?: string | null;
  parentCategory?: string | null;
  parentCategoryIcon?: string | null;
  amount: number;
  percentage: number;
  color: string;
};

export type InvoiceDetailResponse = {
  accountId: string;
  accountName: string;
  accountImage: string | null;
  /** The APPROVED limit — never subtract `creditAvailableLimit` from this; see `CreditCardCockpitAccount`. */
  creditLimit: number | null;
  creditAvailableLimit: number | null;
  /** The limit actually in effect (the holder's own cap when set), the denominator for usage. */
  creditEffectiveLimit: number | null;
  /** Committed amount, from the bank's own open-invoice balance. The numerator. */
  creditUsedAmount: number | null;
  /** False when the limit figures don't reconcile — show available alone, not a percentage. */
  creditLimitReliable: boolean;
  invoice: CreditCardInvoice;
  /** A page of the invoice's charges, newest first: `total` counts them all, `nextOffset` is null on the last page. */
  transactions: Page<ExpenseTransaction>;
  categoryBreakdown: CategoryBreakdownItem[];
};

export type InvoicePaidResponse = {
  accountId: string;
  period: string;
  dueDate: string;
  paidAt: string;
};

export type DebitCategoryBreakdown = {
  category: string;
  amount: number;
  percentage: number;
  color: string;
};

export type CreditCockpitQuery = {
  from?: string;
  to?: string;
  referenceDate?: string;
  period?: string;
};

// Expenses API
export const expensesApi = {
  getCockpit: (
    token: string,
    options: CreditCockpitQuery = {},
    signal?: AbortSignal,
  ) => {
    const params = new URLSearchParams();
    if (options.from) params.set('from', options.from);
    if (options.to) params.set('to', options.to);
    if (options.referenceDate) params.set('referenceDate', options.referenceDate);
    if (options.period) params.set('period', options.period);
    const queryString = params.toString();
    return request<CreditCardCockpitResponse>(
      `/expenses/cockpit${queryString ? `?${queryString}` : ''}`,
      { token, signal },
    );
  },

  getInvoiceDetail: (
    token: string,
    accountId: string,
    year: number,
    month: number,
    position: BillingCyclePosition,
    options?: { limit?: number; offset?: number },
  ) => {
    const params = new URLSearchParams();
    params.set('year', year.toString());
    params.set('month', month.toString());
    params.set('position', position);
    if (options?.limit !== undefined) params.set('limit', options.limit.toString());
    if (options?.offset !== undefined) params.set('offset', options.offset.toString());
    return request<InvoiceDetailResponse>(
      `/expenses/invoices/${accountId}?${params.toString()}`,
      { token },
    );
  },

  // Closed invoices only — the backend resolves closed cycles exclusively and 404s
  // otherwise, so there is no `position` parameter. `dueDate` and `fingerprint` echo
  // what the UI displayed: a different cycle is a 404, a same-cycle change since the
  // view loaded (charges synced meanwhile) is a 409.
  markClosedInvoicePaid: (
    token: string,
    accountId: string,
    year: number,
    month: number,
    dueDate: string,
    fingerprint: string,
  ) => {
    const params = new URLSearchParams();
    params.set('year', year.toString());
    params.set('month', month.toString());
    params.set('dueDate', dueDate);
    params.set('fingerprint', fingerprint);
    return request<InvoicePaidResponse>(
      `/expenses/invoices/${accountId}/paid?${params.toString()}`,
      { method: 'PUT', token },
    );
  },

};

// Transactions API
export type RefundListFilter = 'confirmed' | 'pending';

export type TransactionsQueryOptions = {
  from?: string;
  to?: string;
  accountId?: string;
  type?: string;
  paymentMethod?: string;
  isInstallment?: boolean;
  /** Saídas (expense; `true`) — the VER-258 API param; also excludes internal transfers. */
  isExpense?: boolean;
  /** Entradas = bank income (BANK account + CREDIT + not-expense) — the VER-258 API param; also
   *  excludes internal transfers. */
  isBankIncome?: boolean;
  /** VER-259 Recorrência multi-select: sent as repeated `recurringGroup=` query params
   *  (e.g. `?recurringGroup=fixas&recurringGroup=assinaturas`), OR-unioned by the API (VER-569: it was `tipo`). */
  recurringGroup?: readonly RecurringGroupKey[];
  categoryId?: string | string[];
  parentCategoryId?: string | string[];
  /** confirmed = either side of a refund link; pending = active "previsto" flag. */
  refund?: RefundListFilter;
  /** Purchase channel ("Canal de compra", VER-358): exact `transactions.platform` value. */
  platform?: string;
  /** Tag membership (VER-378), OR-unioned by the API: sent as repeated `tagId=` params. */
  tagId?: string | string[];
  q?: string;
  limit?: number;
  offset?: number;
  sortField?: 'date' | 'value' | 'merchant';
  sortOrder?: 'asc' | 'desc';
};

export type TransactionsMetaOptions = Omit<TransactionsQueryOptions, 'limit' | 'offset' | 'sortField' | 'sortOrder'> & {
  year?: number;
  month?: number;
  /** The user's local calendar date (`YYYY-MM-DD`); an API without the parameter ignores it. */
  today?: string;
};

export type TransactionCategoryBatchRequest = {
  sourceTransactionId: string;
  /** Exactly one of categoryId / customCategoryId. */
  categoryId?: string;
  customCategoryId?: string;
  /**
   * Which anchor the RULE this batch mints should use. The batch itself is always
   * description-scoped; the rule is a separate population, and until VER-334 the two
   * silently disagreed — the batch matched descriptions while the rule anchored to the
   * identity whenever one existed. Omit to keep the server's automatic choice.
   */
  ruleAnchor?: CategoryRuleTarget;
};

export type TransactionCategoryBatchPreview = {
  totalMatches: number;
  updatableCount: number;
  skippedAlreadyCategorizedCount: number;
  exceedsLimit: boolean;
  limit: number;
};

export type TransactionCategoryBatchFailure = {
  transactionId: string;
  message: string;
};

export type TransactionCategoryBatchResult = {
  totalMatches: number;
  attemptedCount: number;
  updatedCount: number;
  skippedAlreadyCategorizedCount: number;
  failedCount: number;
  limit: number;
  updatedTransactions: Transaction[];
  failures: TransactionCategoryBatchFailure[];
};

/** How far a tag or rename batch reaches: every same-description row (the default) or the source row alone. */
export type BatchEditScope = 'description' | 'transaction';

export type TagBatchRequest = {
  sourceTransactionId: string;
  /** The FULL target set — the batch REPLACES each matched row's tags with it. */
  tagIds: string[];
  scope?: BatchEditScope;
};

/** Same envelope as the category batch, so the drawer contract carries over. */
export type TagBatchPreview = {
  totalMatches: number;
  updatableCount: number;
  skippedAlreadyTaggedCount: number;
  exceedsLimit: boolean;
  limit: number;
};

export type TagBatchResult = {
  totalMatches: number;
  updatedCount: number;
  skippedAlreadyTaggedCount: number;
  limit: number;
  /** Ids only — refresh the row caches; the batch write has no per-row failures. */
  updatedTransactionIds: string[];
};

export type RenameBatchRequest = {
  sourceTransactionId: string;
  /** Null = the restore ("Restaurar nome original") over the population. */
  name: string | null;
  scope?: BatchEditScope;
};

export type RenameBatchPreview = {
  totalMatches: number;
  updatableCount: number;
  skippedAlreadyNamedCount: number;
  exceedsLimit: boolean;
  limit: number;
};

export type RenameBatchResult = {
  totalMatches: number;
  updatedCount: number;
  skippedAlreadyNamedCount: number;
  limit: number;
  updatedTransactionIds: string[];
};

/** The tag catalog (VER-378): global (Vértice) + the user's personal tags, name-sorted by
 *  the API. Creation is always personal. */
export const tagsApi = {
  list: (token: string) => request<TransactionTag[]>('/tags', { token }),

  create: (token: string, data: { name: string; color?: string | null }) =>
    request<TransactionTag>('/tags', { method: 'POST', token, body: JSON.stringify(data) }),
};

/** Shared filter → query-param mapping for the transactions list and its meta endpoint (identical
 *  facets; getAll adds paging/sort, getMeta adds year/month). Keeping it in one place is what keeps
 *  list ⟺ meta parity honest. */
function appendTransactionFilters(params: URLSearchParams, options?: TransactionsQueryOptions): void {
  if (options?.from) params.set('from', options.from);
  if (options?.to) params.set('to', options.to);
  if (options?.accountId) params.set('accountId', options.accountId);
  if (options?.type) params.set('type', options.type);
  if (options?.paymentMethod) params.set('paymentMethod', options.paymentMethod);
  if (options?.isInstallment !== undefined) params.set('isInstallment', options.isInstallment.toString());
  if (options?.isExpense !== undefined) params.set('isExpense', options.isExpense.toString());
  if (options?.isBankIncome !== undefined) params.set('isBankIncome', options.isBankIncome.toString());
  if (options?.recurringGroup) options.recurringGroup.forEach((group) => params.append('recurringGroup', group));
  appendParamValues(params, 'categoryId', options?.categoryId);
  appendParamValues(params, 'parentCategoryId', options?.parentCategoryId);
  appendParamValues(params, 'tagId', options?.tagId);
  if (options?.refund) params.set('refund', options.refund);
  if (options?.platform) params.set('platform', options.platform);
  if (options?.q) params.set('q', options.q);
}

export const transactionsApi = {
  getAll: (token: string, options?: TransactionsQueryOptions) => {
    const params = new URLSearchParams();
    appendTransactionFilters(params, options);
    if (options?.limit !== undefined) params.set('limit', options.limit.toString());
    if (options?.offset !== undefined) params.set('offset', options.offset.toString());
    if (options?.sortField) params.set('sortField', options.sortField);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    return request<TransactionsResponse>(`/transactions?${params.toString()}`, { token });
  },

  getMeta: (token: string, options?: TransactionsMetaOptions) => {
    const params = new URLSearchParams();
    appendTransactionFilters(params, options);
    if (options?.year !== undefined) params.set('year', options.year.toString());
    if (options?.month !== undefined) params.set('month', options.month.toString());
    if (options?.today) params.set('today', options.today);
    const qs = params.toString();
    return request<TransactionsMetaResponse>(`/transactions/meta${qs ? `?${qs}` : ''}`, { token });
  },

  // The row plus its details (enrichment, provenance, prior counts) — one read of one thing.
  getOne: async (token: string, id: string) =>
    request<TransactionDetails>(`/transactions/${pathSegment(id)}`, { token }),

  /** Reports a wrong merchant identity (VER-330) — e.g. the geo-contradiction class where
   *  purchase and CNPJ are too far apart for the CNAE to have sold in person. */
  flagMerchant: async (token: string, id: string, note?: string) =>
    request<void>(`/transactions/${pathSegment(id)}/flag-merchant`, {
      method: 'POST',
      token,
      body: JSON.stringify(note ? { note } : {}),
    }),

  update: async (token: string, id: string, data: UpdateTransactionDto) =>
    request<Transaction>(`/transactions/${pathSegment(id)}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify(data),
    }),

  previewCategoryBatch: (token: string, data: TransactionCategoryBatchRequest) =>
    request<TransactionCategoryBatchPreview>('/transactions/category-batch/preview', {
      method: 'POST',
      token,
      body: JSON.stringify(data),
    }),

  updateCategoryBatch: (token: string, data: TransactionCategoryBatchRequest) =>
    request<TransactionCategoryBatchResult>('/transactions/category-batch', {
      method: 'PATCH',
      token,
      body: JSON.stringify(data),
    }),

  /** Replaces one transaction's WHOLE tag set (VER-378): the tag batch scoped to that row. The batch answers
   *  with ids, so the refreshed row is read back after the write. */
  setTags: async (token: string, id: string, tagIds: string[]) => {
    const body: TagBatchRequest = { sourceTransactionId: id, tagIds, scope: 'transaction' };
    await request<TagBatchResult>('/transactions/tag-batch', { method: 'PATCH', token, body: JSON.stringify(body) });
    return transactionsApi.getOne(token, id);
  },

  previewTagBatch: (token: string, data: TagBatchRequest) =>
    request<TagBatchPreview>('/transactions/tag-batch/preview', {
      method: 'POST',
      token,
      body: JSON.stringify(data),
    }),

  applyTagBatch: (token: string, data: TagBatchRequest) =>
    request<TagBatchResult>('/transactions/tag-batch', {
      method: 'PATCH',
      token,
      body: JSON.stringify(data),
    }),

  /** Sets (or, with null, clears — "Restaurar nome original") the display name (VER-377): the rename batch
   *  scoped to that row. The original description is untouched. The batch answers with ids, so the refreshed
   *  row is read back after the write. */
  rename: async (token: string, id: string, name: string | null) => {
    const body: RenameBatchRequest = { sourceTransactionId: id, name, scope: 'transaction' };
    await request<RenameBatchResult>('/transactions/description-batch', { method: 'PATCH', token, body: JSON.stringify(body) });
    return transactionsApi.getOne(token, id);
  },

  previewRenameBatch: (token: string, data: RenameBatchRequest) =>
    request<RenameBatchPreview>('/transactions/description-batch/preview', {
      method: 'POST',
      token,
      body: JSON.stringify(data),
    }),

  applyRenameBatch: (token: string, data: RenameBatchRequest) =>
    request<RenameBatchResult>('/transactions/description-batch', {
      method: 'PATCH',
      token,
      body: JSON.stringify(data),
    }),
};


// Linked refunds API
export type TransactionRefundCandidate = {
  transactionId: string;
  description: string;
  merchantName?: string | null;
  amount: number;
  date: string;
  accountId: string;
  accountName: string;
  accountType: string;
  connectorName?: string | null;
  matchTier?: string | null;
  /** Magnitude still allocatable: |amount| minus what is already linked on this row's side.
   *  Both sides fan out, so a candidate can be partly spent — use THIS for consequence copy,
   *  not `amount`, which is only the row's face value. */
  availableAmount?: number;
};

// The review queue and the refund candidates answer `results` today and `Page<T>`'s `items` once the API pages them
// (VER-569): the screens read `results` either way, whichever API release answers.
type ResultsOrItems<T extends { results: unknown[] }> = Omit<T, 'results'> & { results?: T['results']; items?: T['results'] };

/** The review queue items one read asks for: what a batch can settle at once. */
export const REVIEW_QUEUE_PAGE_SIZE = 100;

function withResults<T extends { results: unknown[] }>(page: ResultsOrItems<T>): T {
  const { items, ...rest } = page;
  return { ...rest, results: page.results ?? items ?? [] } as unknown as T;
}

export type RefundCandidatesResponse = {
  /** Top-ranked candidates (≤ 5), rendered under "Sugestões". */
  suggestions: TransactionRefundCandidate[];
  /** The 90-day window page ("Outros lançamentos · 90 dias"). */
  results: TransactionRefundCandidate[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
};

export type RefundLinkDto = {
  id: string;
  expenseTransactionId: string;
  refundTransactionId: string;
  /** Neutralized portion locked at link time: min(|expense|, |refund|). */
  amount: number;
  createdAt: string;
};

export const refundsApi = {
  getCandidates: (
    token: string,
    transactionId: string,
    options?: { q?: string; limit?: number; offset?: number; excludeLinkId?: string },
  ) => {
    const params = new URLSearchParams();
    if (options?.q) params.set('q', options.q);
    if (options?.limit !== undefined) params.set('limit', options.limit.toString());
    if (options?.offset !== undefined) params.set('offset', options.offset.toString());
    // Relink only: the link being re-pointed releases its allocation, so the anchor's
    // remaining capacity (which drives ranking) must not count it.
    if (options?.excludeLinkId) params.set('excludeLinkId', options.excludeLinkId);
    const qs = params.toString();
    return request<ResultsOrItems<RefundCandidatesResponse>>(
      `/transactions/${transactionId}/refund-candidates${qs ? `?${qs}` : ''}`,
      { token },
    ).then(withResults<RefundCandidatesResponse>);
  },

  link: (token: string, data: { expenseTransactionId: string; refundTransactionId: string }) =>
    request<RefundLinkDto>('/refund-links', {
      method: 'POST',
      token,
      body: JSON.stringify(data),
    }),

  // Exactly one of the two sides may be replaced per call.
  relink: (
    token: string,
    linkId: string,
    data: { expenseTransactionId?: string; refundTransactionId?: string },
  ) =>
    request<RefundLinkDto>(`/refund-links/${linkId}`, {
      method: 'PUT',
      token,
      body: JSON.stringify(data),
    }),

  unlink: (token: string, linkId: string) =>
    request<void>(`/refund-links/${linkId}`, { method: 'DELETE', token }),

  // POST flags, DELETE removes the flag; both answer nothing (204), so the
  // updated row is read back for the callers that replace it in their caches.
  setExpectedRefund: async (token: string, transactionId: string, flagged: boolean) => {
    await request<void>(`/transactions/${transactionId}/expected-refund`, {
      method: flagged ? 'POST' : 'DELETE',
      token,
    });
    return transactionsApi.getOne(token, transactionId);
  },

  // Resolves a "possível reembolso" review item by dismissing only the proposal —
  // a category review item for the same transaction stays in the queue.
  dismissRefundSuggestion: (token: string, transactionId: string) =>
    request<void>(`/transactions/${transactionId}/refund-suggestion`, {
      method: 'DELETE',
      token,
    }),
};

// Recurring payments API
export type RecurringPaymentCadence = 'Unknown' | 'Weekly' | 'Monthly' | 'Yearly';
export type RecurringPaymentKind = 'Subscription' | 'Installment' | 'PixRecurring' | 'Utility' | 'Financing';
export type RecurringPaymentStatus =
  | 'Detected'
  | 'Active'
  | 'Cancelling'
  | 'Cancelled'
  | 'FailedCancellation'
  | 'Completed'
  | 'Rejected'
  | 'Missed'
  | 'EarlySettlementSuspected';
export type RecurringPaymentSortField = 'date' | 'value' | 'description';

export interface RecurringPaymentCategoryFacet {
  categoryId: string;
  label: string;
  icon: string | null;
  count: number;
  monthlyTotal: number;
  activeCount: number;
  yearlyTotal: number;
}

/**
 * What a series costs in the currency it is actually priced in. Absent (or BRL) means the
 * ordinary case: the charge is denominated in reais and `amount` is the whole story.
 *
 * Cross-repo contract: the backend does not emit this yet. Until it does, every consumer falls
 * back to today's BRL-only rendering — the field is optional precisely so the client can ship
 * ahead of the API.
 */
export interface RecurringPaymentPrice {
  amount: number;
  currencyCode: string;
  /** Rate behind the latest conversion, for the detail drawer's history. */
  fxRate?: number | null;
  /**
   * False when the bank has not reported a value in the account currency. The series' `amount` is
   * then 0 (see its own doc), so a surface that wants to SAY the value is pending reads this flag;
   * one that only sums `amount` needs no change.
   */
  converted: boolean;
}

export interface RecurringPayment {
  id: string;
  merchantName: string | null;
  merchantCnpj: string | null;
  merchantCategory: string | null;
  normalizedKey: string;
  /**
   * Always a figure in the ACCOUNT currency (BRL) — the one every total sums. It never carries an
   * origin-currency value: when the series is priced abroad and the bank has not reported a
   * conversion (`price.converted === false`), the server sends 0. That keeps every existing
   * consumer correct by construction — summing `amount` can never mistake dollars for reais — and
   * only surfaces that want to say "sem conversão" need to read `price` (see `hasConvertedAmount`).
   */
  amount: number;
  /** Set only when the series is priced abroad; see RecurringPaymentPrice. */
  price?: RecurringPaymentPrice | null;
  cadence: RecurringPaymentCadence;
  dayOfMonth: number | null;
  firstSeen: string;
  lastSeen: string;
  occurrences: number;
  kind: RecurringPaymentKind;
  status: RecurringPaymentStatus;
  confidence: number;
  userConfirmed: boolean | null;
  nextExpectedDate: string | null;
  paymentMethod: string | null;
  accountId: string | null;
  category: string | null;
  categoryId: string | null;
  categoryTranslated?: string | null;
  categoryIcon?: string | null;
  parentCategoryTranslated?: string | null;
  parentCategoryIcon?: string | null;
  /** Overview only: the newest linked charge that settled this period's occurrence, and the total
   * those charges moved. Ground truth for "already paid" — `lastSeen` is a detector output and lags
   * whenever a charge fails a clustering gate. */
  settledThisPeriodAt?: string | null;
  settledThisPeriodAmount?: number | null;
  /** Overview only: exactly what this series contributed to its group's `monthlyTotal`. Not always
   * `amount` — weekly is scaled by weeks-per-month, yearly contributes nothing outside its
   * anniversary month — so net a settlement against this, never against `amount`. */
  cycleForecastAmount?: number | null;
  installmentCurrent: number | null;
  installmentTotal: number | null;
  installmentRemaining: number | null;
  expectedEndDate: string | null;
  cancellingMarkedAt: string | null;
  cancelledAt: string | null;
  failedChargeAt: string | null;
  cancellationUrl: string | null;
}

export interface RecurringPaymentListResponse {
  items: RecurringPayment[];
  monthlyBurn: number;
  activeCount: number;
  detectedCount: number;
  failedCancellationCount: number;
  pendingThisMonth: number;
  upcomingCharges: RecurringPayment[];
  total?: number;
  limit?: number;
  offset?: number;
  hasMore?: boolean;
  nextOffset?: number | null;
  categories?: RecurringPaymentCategoryFacet[] | null;
}

export type RecurringPaymentQueryOptions = {
  status?: RecurringPaymentStatus | RecurringPaymentStatus[];
  kind?: RecurringPaymentKind;
  categoryId?: string | string[];
  sortField?: RecurringPaymentSortField;
  sortOrder?: 'asc' | 'desc';
  upcomingLimit?: number;
  limit?: number;
  offset?: number;
};

export type RecurringGroupKey = 'parcelamentos' | 'assinaturas' | 'fixas';

export interface RecurringTypeSlice {
  key: RecurringGroupKey;
  value: number;
}

export interface RecurringPeriodSummary {
  year: number;
  month: number;
  previsto: number;
  pago: number;
  breakdown: RecurringTypeSlice[];
}

export interface RecurringMonthlyPoint {
  label: string;
  monthIndex: number;
  parcelamentos: number;
  assinaturas: number;
  fixas: number;
}

export interface RecurringGroup {
  key: RecurringGroupKey;
  monthlyTotal: number;
  count: number;
  items: RecurringPayment[];
}

export interface RecurringRunoffSegment {
  id: string;
  label: string;
  amount: number;
}

export interface RecurringRunoffMonth {
  month: string;
  monthLabel: string;
  total: number;
  /** One slice per installment, so the run-off chart can stack by parcela. */
  segments: RecurringRunoffSegment[];
}

export interface RecurringOverviewResponse {
  period: RecurringPeriodSummary;
  year: RecurringMonthlyPoint[];
  groups: RecurringGroup[];
  currentMonthIndex: number;
  /** Per-month installment run-off split by parcela; totals reconcile with the faturas ladder. */
  runoff: RecurringRunoffMonth[];
}

function buildRecurringPaymentParams(options?: RecurringPaymentQueryOptions) {
  const params = new URLSearchParams();
  appendParamValues(params, 'status', options?.status);
  if (options?.kind) params.set('kind', options.kind);
  appendParamValues(params, 'categoryId', options?.categoryId);
  if (options?.sortField) params.set('sortField', options.sortField);
  if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
  if (options?.upcomingLimit !== undefined) params.set('upcomingLimit', options.upcomingLimit.toString());
  if (options?.limit !== undefined) params.set('limit', options.limit.toString());
  if (options?.offset !== undefined) params.set('offset', options.offset.toString());
  return params;
}

export const recurringPaymentsApi = {
  // `scope` is the CLIENT's calendar (local year/month/today); without it the server uses UTC.
  getAll: (
    token: string,
    options?: RecurringPaymentQueryOptions,
    scope?: CalendarScopeQuery,
  ) => {
    const params = buildRecurringPaymentParams(options);
    appendCalendarScope(params, scope);
    const qs = params.toString();
    return request<RecurringPaymentListResponse>(`/recurring-payments${qs ? `?${qs}` : ''}`, { token });
  },

  /** A page of the series' charges, newest first: `total` counts them all, `nextOffset` is null on the last page. */
  getTransactions: (token: string, id: string, options?: { limit?: number; offset?: number }) => {
    const params = new URLSearchParams();
    if (options?.limit !== undefined) params.set('limit', options.limit.toString());
    if (options?.offset !== undefined) params.set('offset', options.offset.toString());
    const qs = params.toString();
    return request<Page<Transaction>>(
      `/recurring-payments/${id}/transactions${qs ? `?${qs}` : ''}`,
      { token },
    );
  },

  update: (
    token: string,
    id: string,
    data: {
      status?: RecurringPaymentStatus;
      userConfirmed?: boolean | null;
      kind?: RecurringPaymentKind;
      cadence?: RecurringPaymentCadence;
      category?: string | null;
      categoryId?: string | null;
      accountId?: string | null;
      cancellationUrl?: string | null;
    },
  ) =>
    request<RecurringPayment>(`/recurring-payments/${id}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify(data),
    }),

  // `scope` is the CLIENT's calendar (local year/month/today); without it the server uses UTC.
  getOverview: (token: string, scope?: CalendarScopeQuery) =>
    request<RecurringOverviewResponse>(`/recurring-payments/overview${calendarScopeSearch(scope)}`, { token }),

  delete: (token: string, id: string) =>
    request<void>(`/recurring-payments/${id}`, { method: 'DELETE', token }),

  rescan: (token: string) =>
    request<{ detected: number }>(`/recurring-payments/rescan`, { method: 'POST', token }),
};

export type InvestmentsPage = {
  /** A page of positions: `total` counts every position the filters match. */
  positions: Page<InvestmentItem>;
  /** Facet over the user's whole portfolio (unfiltered) — powers the institution filter chips. */
  institutions: InstitutionFacet[];
  /** The whole-portfolio summary, present only when the request set `includeSummary`. */
  summary: InvestmentSummary | null;
};

/** The roles a user can assign — PRIMARY is the group's own anchor and is never chosen. */
export type DuplicateDecisionRole = Exclude<InvestmentSourceRole, 'PRIMARY'>;

/** A decision on a proposed duplicate, in the review card's vocabulary. */
export type DuplicateReviewDecision = 'mirror' | 'additive' | 'not_duplicate';

const DUPLICATE_DECISION_WIRE: Record<DuplicateReviewDecision, string> = {
  mirror: 'MIRROR',
  additive: 'ADDITIVE',
  not_duplicate: 'NOT_DUPLICATE',
};

// Investments API
export const investmentsApi = {
  /** A page of positions; `includeSummary` adds the whole-portfolio summary to it. */
  getAll: (
    token: string,
    options?: {
      limit?: number;
      offset?: number;
      sortField?: string;
      sortOrder?: 'asc' | 'desc';
      q?: string;
      category?: string | string[];
      exclude?: boolean;
      indexer?: string;
      liquidity?: string;
      institution?: string | string[];
      includeWithdrawn?: boolean;
      includeSummary?: boolean;
    },
  ) => {
    const params = new URLSearchParams();
    if (options?.limit !== undefined) params.set('limit', options.limit.toString());
    if (options?.offset !== undefined) params.set('offset', options.offset.toString());
    if (options?.sortField) params.set('sortField', options.sortField);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.q) params.set('q', options.q);
    appendParamValues(params, 'category', options?.category);
    if (options?.exclude !== undefined) params.set('exclude', options.exclude.toString());
    if (options?.indexer) params.set('indexer', options.indexer);
    if (options?.liquidity) params.set('liquidity', options.liquidity);
    appendParamValues(params, 'institution', options?.institution);
    if (options?.includeWithdrawn) params.set('includeWithdrawn', 'true');
    if (options?.includeSummary) params.set('includeSummary', 'true');
    const qs = params.toString();
    return request<InvestmentsPage>(`/investments${qs ? `?${qs}` : ''}`, { token });
  },

  // Every ungrouped position across the whole portfolio, as the picker lists them: the route answers
  // a page at a time (up to 200), so the wrapper reads the pages through and no position is out of
  // the picker's reach. Until core.api's VER-581 is live the route answers the bare list, the whole.
  getGroupingCandidates: async (token: string, q?: string) => {
    const candidates: InvestmentItem[] = [];
    let offset = 0;
    for (;;) {
      const params = new URLSearchParams({ limit: String(GROUPING_CANDIDATES_PAGE), offset: String(offset) });
      if (q) params.set('q', q);
      const answer = await request<Page<InvestmentItem> | InvestmentItem[]>(
        `/investments/grouping-candidates?${params.toString()}`,
        { token },
      );
      if (Array.isArray(answer)) return answer;
      candidates.push(...answer.items);
      if (!answer.hasMore || answer.nextOffset === null || answer.items.length === 0) return candidates;
      offset = answer.nextOffset;
    }
  },

  getDetail: (token: string, id: string, options?: { limit?: number; offset?: number }) => {
    const params = new URLSearchParams();
    if (options?.limit !== undefined) params.set('limit', options.limit.toString());
    if (options?.offset !== undefined) params.set('offset', options.offset.toString());
    const qs = params.toString();
    return request<InvestmentDetail>(`/investments/${id}/transactions${qs ? `?${qs}` : ''}`, { token });
  },


  // One decision per proposed pair: mirror / additive confirm it with that role,
  // not_duplicate rejects it for good.
  decideDuplicate: (token: string, memberId: string, decision: DuplicateReviewDecision) =>
    request<void>(`/investments/duplicates/${pathSegment(memberId)}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify({ decision: DUPLICATE_DECISION_WIRE[decision] }),
    }),

  mergeInvestments: (token: string, investmentIds: string[], role: DuplicateDecisionRole) =>
    request<{ groupId: string; primaryInvestmentId: string }>('/investments/groups', {
      method: 'POST',
      token,
      body: JSON.stringify({ investmentIds, role }),
    }),

  // Detaches by the membership's id (a source's `memberId`), the same id a decision on the
  // proposal takes. Until core.api's VER-581 is live the route takes the investment's id.
  unmergeInvestment: (token: string, groupId: string, memberId: string) =>
    request<void>(`/investments/groups/${pathSegment(groupId)}/members/${pathSegment(memberId)}`, {
      method: 'DELETE',
      token,
    }),
};

// Home API
export const homeApi = {
  getSummary: (token: string, options?: { year?: number; month?: number; from?: string; to?: string }) => {
    const params = new URLSearchParams();
    if (options?.year !== undefined) params.set('year', String(options.year));
    if (options?.month !== undefined) params.set('month', String(options.month));
    if (options?.from) params.set('from', options.from);
    if (options?.to) params.set('to', options.to);
    const qs = params.toString();
    return request<HomeSummary>(`/home/summary${qs ? `?${qs}` : ''}`, { token });
  },

  // Home widget endpoints — current-cycle scoped. The cycle is the CLIENT's calendar
  // (`CalendarScopeQuery`: local year/month/today); without it the server uses its UTC month.
  getSpendingPace: (token: string, scope?: CalendarScopeQuery) =>
    request<SpendingPace>(`/home/spending-pace${calendarScopeSearch(scope)}`, { token }),

  getOpenInvoices: (token: string, scope?: CalendarScopeQuery) =>
    request<OpenInvoicesWidget>(`/home/open-invoices${calendarScopeSearch(scope)}`, { token }),

  getNetWorthTrend: (token: string, scope?: CalendarScopeQuery) =>
    request<NetWorthTrendWidget>(`/home/net-worth${calendarScopeSearch(scope)}`, { token }),

  getAvailableToSpend: (token: string, scope?: CalendarScopeQuery) =>
    request<AvailableToSpend>(`/home/available-to-spend${calendarScopeSearch(scope)}`, { token }),
};

// Goals API
export const goalsApi = {
  getOverview: (token: string) =>
    request<GoalsOverview>('/goals', { token }),

  getById: (token: string, id: string) =>
    request<GoalDetail>(`/goals/${id}`, { token }),

  create: (token: string, data: CreateGoalDto) =>
    request<GoalDetail>('/goals', {
      method: 'POST',
      token,
      body: JSON.stringify(data),
    }),

  update: (token: string, id: string, data: UpdateGoalDto) =>
    request<GoalDetail>(`/goals/${id}`, {
      method: 'PUT',
      token,
      body: JSON.stringify(data),
    }),

  delete: async (token: string, id: string) => {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
    const response = await fetch(`${API_URL}/goals/${id}`, { method: 'DELETE', headers });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new ApiError(response.status, readApiErrorMessage(error), error);
    }
  },

  getLinkableEntities: (token: string) =>
    request<LinkableEntity[]>('/goals/linkable-entities', { token }),

  getHistory: (token: string, id: string) =>
    request<GoalContributionHistoryEntry[]>(`/goals/${id}/history`, { token }),
};

// Problem reports API (Contract A)
export const problemReportsApi = {
  create: (token: string, data: CreateProblemReportRequest) =>
    request<void>('/problem-reports', {
      method: 'POST',
      token,
      body: JSON.stringify(data),
    }),
};

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export const categoriesApi = {
  getAll: (token: string, options?: { usedSince?: Date }) => {
    const qs = options?.usedSince ? `?usedSince=${encodeURIComponent(options.usedSince.toISOString())}` : '';
    return request<ParentCategory[]>(`/categories${qs}`, { token });
  },
};

// Unified Orçamento page (VER-246): category overview + effective-from limits +
// custom categories + the "A revisar" queue.
export const categoryBudgetApi = {
  getOverview: (
    token: string,
    scope: { year?: number; month?: number; from?: Date; to?: Date; budgetMonths?: number; today?: string } = {},
  ) => {
    const params = new URLSearchParams();
    if (scope.from && scope.to) {
      params.set('from', toIsoDate(scope.from));
      params.set('to', toIsoDate(scope.to));
      if (scope.budgetMonths !== undefined) params.set('budgetMonths', String(scope.budgetMonths));
    } else {
      if (scope.year) params.set('year', scope.year.toString());
      if (scope.month) params.set('month', scope.month.toString());
    }
    if (scope.today) params.set('today', scope.today);
    const qs = params.toString();
    return request<CategoryOverviewResponse>(`/categories/overview${qs ? `?${qs}` : ''}`, { token });
  },

  upsertLimit: (token: string, categoryRef: string, data: UpsertCategoryLimitRequest) =>
    request<void>(`/categories/${encodeURIComponent(categoryRef)}/limit`, {
      method: 'PUT',
      token,
      body: JSON.stringify(data),
    }),

  // Income category → meta (display-only); null goalId removes the link.
  linkGoal: (token: string, categoryRef: string, data: UpsertCategoryGoalLinkRequest) =>
    request<void>(`/categories/${encodeURIComponent(categoryRef)}/goal`, {
      method: 'PUT',
      token,
      body: JSON.stringify(data),
    }),

  getCustomCategories: (token: string) =>
    request<CustomCategorySummary[]>('/categories/custom', { token }),

  createCategory: (token: string, data: CreateCustomCategoryRequest) =>
    request<{ id: string }>('/categories', {
      method: 'POST',
      token,
      body: JSON.stringify(data),
    }),

  // The answer is an envelope: `suggestion` is null when there is nothing to suggest (a
  // name too short, the AI unconfigured), which resolves to undefined. Until core.api's
  // VER-574 is live the route answers the suggestion itself or 204, which read the same.
  suggestCategory: async (
    token: string,
    data: SuggestCategoryRequest,
    signal?: AbortSignal,
  ): Promise<CategorySuggestion | undefined> => {
    const answer = await request<{ suggestion: CategorySuggestion | null } | CategorySuggestion | undefined>(
      '/categories/suggest',
      { method: 'POST', token, body: JSON.stringify(data), signal },
    );
    if (!answer) return undefined;
    return 'suggestion' in answer ? (answer.suggestion ?? undefined) : answer;
  },

  // The answer is an envelope: `match` is null when nothing is similar enough (or the AI
  // is unconfigured), which resolves to undefined; until core.api's VER-574 is live the
  // route answers the match itself or 204, which read the same. `id` is renamed to
  // `categoryId` to match the picker's CategoryOption shape.
  suggestSimilarCategory: async (
    token: string,
    data: SuggestSimilarCategoryRequest,
    signal?: AbortSignal,
  ): Promise<SimilarCategorySuggestion | undefined> => {
    // parentLabel is a C# `string?` — ASP.NET's default JSON options don't omit nulls, so
    // the wire value is `null` (present), never an absent key; model it as such and
    // normalize to `undefined` here so the exported SimilarCategorySuggestion contract
    // (parentLabel?: string) is actually true of the value this function returns.
    type SimilarMatch = { id: string; source: string; label: string; parentLabel: string | null };
    const answer = await request<{ match: SimilarMatch | null } | SimilarMatch | undefined>(
      '/categories/suggest-similar',
      { method: 'POST', token, body: JSON.stringify(data), signal },
    );
    const match = answer && 'match' in answer ? answer.match : answer;
    return match
      ? {
          categoryId: match.id,
          source: match.source as SimilarCategorySuggestion['source'],
          label: match.label,
          parentLabel: match.parentLabel ?? undefined,
        }
      : undefined;
  },

  updateCategory: (token: string, id: string, data: UpdateCustomCategoryRequest) =>
    request<void>(`/categories/${encodeURIComponent(id)}`, {
      method: 'PUT',
      token,
      body: JSON.stringify(data),
    }),

  getRules: (token: string, categoryRef?: string) =>
    request<CategoryRule[]>(
      `/category-rules${categoryRef ? `?categoryRef=${encodeURIComponent(categoryRef)}` : ''}`,
      { token },
    ),

  deleteRule: (token: string, ruleId: string) =>
    request<void>(`/category-rules/${encodeURIComponent(ruleId)}`, { method: 'DELETE', token }),

  // Period-independent: pending review is a to-do list, not a view of the selected
  // period (one page of it; total carries the real count, scoped to `kind`). The page asks for
  // REVIEW_QUEUE_PAGE_SIZE items: the queue's cap until the API pages it, the most a batch takes, and
  // twice the API's default page once it does (VER-569). An API that doesn't page yet ignores `limit`.
  getReviewQueue: (token: string, kind?: 'category' | 'refund' | 'investment') => {
    const params = new URLSearchParams({ limit: REVIEW_QUEUE_PAGE_SIZE.toString() });
    if (kind) params.set('kind', kind);
    return request<ResultsOrItems<ReviewQueueResponse>>(`/transactions/review-queue?${params}`, { token })
      .then(withResults<ReviewQueueResponse>);
  },

  confirmCategory: (token: string, transactionId: string, data: ConfirmCategoryRequest) =>
    request<ConfirmCategoryResult>(`/transactions/${transactionId}/confirm-category`, {
      method: 'POST',
      token,
      body: JSON.stringify(data),
    }),

  // "Confirmar todos": the whole queue as-is, any date and uncapped — the batch route's `all`.
  confirmAll: (token: string) =>
    request<{ confirmed: number; failed: number }>('/transactions/review-queue/batch', {
      method: 'POST',
      token,
      body: JSON.stringify({ all: true }),
    }),

  // Batch on a SELECTION: no categoryRef confirms the chosen items as-is; with one, all
  // of them are assigned to it and confirmed (the batch "Alterar").
  reviewBatch: (token: string, data: { transactionIds: string[]; categoryRef?: string | null }) =>
    request<{ confirmed: number; failed: number }>('/transactions/review-queue/batch', {
      method: 'POST',
      token,
      body: JSON.stringify(data),
    }),
};

// Subscriptions / Billing API
export type PlanDto = {
  id: string;
  code: string;
  name: string;
  unitAmountCents: number;
  currency: string;
  imageUrl: string | null;
  maxBankConnections: number | null;
  maxGoals: number | null;
  maxActiveBudgets: number | null;
  transactionHistoryDays: number | null;
  maxAiMessagesPerMonth: number | null;
  hasExport: boolean;
  hasAdvancedAnalytics: boolean;
  hasInvestmentsFull: boolean;
  sortOrder: number;
  canCheckout: boolean;
};

export type EntitlementsDto = {
  maxBankConnections: number | null;
  maxGoals: number | null;
  maxActiveBudgets: number | null;
  transactionHistoryDays: number | null;
  maxAiMessagesPerMonth: number | null;
  hasExport: boolean;
  hasAdvancedAnalytics: boolean;
  hasInvestmentsFull: boolean;
};

export type SubscriptionDto = {
  planCode: string;
  planName: string;
  status: string;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;
  trialEndsAt: string | null;
  hasManagedSubscription: boolean;
  plan: PlanDto;
  entitlements: EntitlementsDto;
};

export type BillingInvoiceDto = {
  id: string;
  stripeInvoiceId: string;
  hasHostedInvoice: boolean;
  amountDueCents: number;
  amountPaidCents: number;
  currency: string;
  status: string;
  periodStart: string | null;
  periodEnd: string | null;
  paidAt: string | null;
  createdAt: string;
};

export const subscriptionsApi = {
  me: (token: string) => request<SubscriptionDto>('/subscriptions/me', { token }),

  plans: (token: string) => request<PlanDto[]>('/subscriptions/plans', { token }),

  startCheckout: (token: string, planCode: string) =>
    request<{ url: string }>('/subscriptions/checkout', {
      method: 'POST',
      token,
      body: JSON.stringify({ planCode }),
    }),

  syncCheckoutSession: (token: string, sessionId: string) =>
    request<SubscriptionDto>('/subscriptions/checkout/sync', {
      method: 'POST',
      token,
      body: JSON.stringify({ sessionId }),
    }),

  openPortal: (token: string) =>
    request<{ url: string }>('/subscriptions/portal', { method: 'POST', token }),

  invoiceHostedUrl: (token: string, invoiceId: string) =>
    request<{ url: string }>(`/subscriptions/invoices/${encodeURIComponent(invoiceId)}/hosted`, { token }),

  // A page (20 by default) of the person's invoices, newest first; the screen shows one page,
  // so the wrapper answers its rows. Until core.api's VER-582 is live the route answers the
  // bare list, which reads the same.
  invoices: async (token: string, options?: { limit?: number }) => {
    const params = new URLSearchParams();
    if (options?.limit !== undefined) params.set('limit', options.limit.toString());
    const qs = params.toString();
    const answer = await request<{ items: BillingInvoiceDto[] } | BillingInvoiceDto[]>(
      `/subscriptions/invoices${qs ? `?${qs}` : ''}`,
      { token },
    );
    return Array.isArray(answer) ? answer : answer.items;
  },
};

// Onboarding flow (VER-368): the server-side resumable position, the consent
// acceptance record and the consent-gated completion. Only the POSITION lives on
// this resource — answers live on their own resources (items, classification,
// recurring payments).
export const onboardingApi = {
  getState: (token: string) => request<OnboardingState>('/onboarding/state', { token }),

  updateState: (token: string, data: UpdateOnboardingStateRequest) =>
    request<OnboardingState>('/onboarding/state', {
      method: 'PUT',
      token,
      body: JSON.stringify(data),
    }),

  complete: (token: string) =>
    request<OnboardingState>('/onboarding/complete', { method: 'POST', token }),
};

// Classification engine (VER-366): the ranked streams worth asking about plus the
// three-class totals, and the confirmation write path (one answered stream card).
export const classificationApi = {
  getStreamCandidates: (token: string) =>
    request<StreamCandidatesResponse>('/classification/stream-candidates', { token }),

  confirmStream: (token: string, data: StreamConfirmationRequest) =>
    request<StreamConfirmationResponse>('/classification/stream-confirmations', {
      method: 'POST',
      token,
      body: JSON.stringify(data),
    }),
};

// Declared external accounts (VER-363/365) — the "account I own but can't connect"
// perimeter declaration the "É renda" follow-up records.
export const externalAccountsApi = {
  getAll: (token: string) =>
    request<DeclaredExternalAccount[]>('/external-accounts/', { token }),

  declare: (token: string, name: string) =>
    request<DeclaredExternalAccount>('/external-accounts/', {
      method: 'POST',
      token,
      body: JSON.stringify({ name }),
    }),
};

// The semantic-axis write path (VER-361): a deliberate user decision on WHAT a
// transaction is, recorded with provenance — never by mutating the category.
export const semanticClassApi = {
  setBatch: (token: string, transactionIds: string[], semanticClass: SemanticClassWire | null) =>
    request<SemanticClassUpdateResult>('/transactions/semantic-class', {
      method: 'PATCH',
      token,
      body: JSON.stringify({ transactionIds, semanticClass }),
    }),
};

export const notificationsApi = {
  // 404 = notifications are not configured in this environment; callers hide the surface.
  issueToken: (token: string) =>
    request<NotificationTokenResponse>('/users/me/notifications/token', {
      method: 'POST',
      token,
    }),
};

// Unare no Claude e no ChatGPT (VER-492): the connector section of Perfil. `enabled` is false
// while the connector is off in this environment; the page then hides the section.
export type McpConnectedAppDto = {
  name: string;
  firstUsedAt: string;
  lastUsedAt: string;
};

export type McpSettingsDto = {
  enabled: boolean;
  /** Unused since VER-503: every plan runs the connector, and the API sends `true` while it is on. */
  pro: boolean;
  serverUrl: string | null;
  apps: McpConnectedAppDto[];
};

export const mcpApi = {
  settings: (token: string) => request<McpSettingsDto>('/mcp/settings', { token }),
};

export const spacesApi = {
  ledger: (token: string, id: string, periodKey: string) => request<SpaceLedger>(`/spaces/${encodeURIComponent(id)}/ledger?${new URLSearchParams({ periodKey })}`, { token }),
  budget: (token: string, id: string, periodKey: string | null) => request<SpaceBudget>(`/spaces/${encodeURIComponent(id)}/budget${periodKey ? `?${new URLSearchParams({ periodKey })}` : ''}`, { token }),
  setBudget: (token: string, id: string, body: SetSpaceBudgetRequest) => request<SpaceBudget>(`/spaces/${encodeURIComponent(id)}/budget`, { method: 'PUT', body: JSON.stringify(body), token }),
  commitments: (token: string, id: string, periodKey: string) => request<SpaceCommitments>(`/spaces/${encodeURIComponent(id)}/commitments?${new URLSearchParams({ periodKey })}`, { token }),
  audit: (token: string, id: string, offset: number, limit: number) => request<SpaceAuditHistory>(`/spaces/${encodeURIComponent(id)}/audit?${new URLSearchParams({ limit: String(limit), offset: String(offset) })}`, { token }),
  settlementHistory: (token: string, id: string, offset: number) => request<SpaceSettlementHistory>(`/spaces/${encodeURIComponent(id)}/settlements?${new URLSearchParams({ limit: '5', offset: String(offset) })}`, { token }),
  settlementReceipt: (token: string, id: string, receiptId: string) => request<SpaceSettlementReceipt>(`/spaces/${encodeURIComponent(id)}/settlements/${encodeURIComponent(receiptId)}`, { token }),
  registerSettlement: (token: string, id: string, body: RegisterSpaceSettlement) => request<SpaceSettlementReceipt>(`/spaces/${encodeURIComponent(id)}/settlements`, { token, method: 'POST', body: JSON.stringify(body) }),
  expensesOverview: (token: string, id: string, params: Record<string, string>) => request<SpaceExpensesOverview>(`/spaces/${encodeURIComponent(id)}/expenses/overview?${new URLSearchParams(params)}`, { token }),
  removeExpense: (token: string, id: string, expenseId: string, exception: boolean) => request<void>(`/spaces/${encodeURIComponent(id)}/expenses/${encodeURIComponent(expenseId)}${exception ? '?exception=true' : ''}`, { token, method: 'DELETE' }),
  sendRules: (token: string) => request<MySpaceSendRules>('/spaces/send-rules', { token }),
  rulesOverview: (token: string) => request<MySpaceRulesOverview>('/spaces/send-rules/overview', { token }),
  setRuleEnabled: (token: string, id: string, enabled: boolean) => request<SpaceSendRule>(`/spaces/send-rules/${encodeURIComponent(id)}`, { token, method: 'PATCH', body: JSON.stringify({ enabled }) }),
  previewSend: (token: string, id: string, transactionId: string) => request<SpaceBatchPreview>(`/spaces/${encodeURIComponent(id)}/expenses/batch-preview?${new URLSearchParams({ transactionId })}`, { token }),
  send: (token: string, id: string, body: SendToSpaceRequest) => request<SendToSpaceResult>(`/spaces/${encodeURIComponent(id)}/expenses`, { token, method: 'POST', body: JSON.stringify(body) }),
  create: (token: string, body: CreateSpaceRequest) => request<SpaceDetail>('/spaces', { token, method: 'POST', body: JSON.stringify(body) }),
  invites: (token: string) => request<MySpaceInvite[]>('/spaces/invites', { token }),
  // Accepted answers the space it joined; declined answers nothing (204).
  answerInvite: (token: string, id: string, body: AnswerSpaceInviteRequest) => request<SpaceDetail | undefined>(`/spaces/invites/${encodeURIComponent(id)}`, { token, method: 'PATCH', body: JSON.stringify(body) }),
  invite: (token: string, id: string, body: SpaceInviteRequest) => request<SpaceInvite>(`/spaces/${encodeURIComponent(id)}/invites`, { token, method: 'POST', body: JSON.stringify(body) }),
  revokeInvite: (token: string, id: string, inviteId: string) => request<void>(`/spaces/${encodeURIComponent(id)}/invites/${encodeURIComponent(inviteId)}`, { token, method: 'DELETE' }),
  setPercentages: (token: string, id: string, body: { members?: { userId: string; pct: number }[]; equalize?: boolean }) => request<SpaceDetail>(`/spaces/${encodeURIComponent(id)}/percentages`, { token, method: 'PUT', body: JSON.stringify(body) }),
  updateMember: (token: string, id: string, userId: string, body: { role: SpaceRole }) => request<SpaceDetail>(`/spaces/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`, { token, method: 'PATCH', body: JSON.stringify(body) }),
  removeMember: (token: string, id: string, userId: string) => request<void>(`/spaces/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`, { token, method: 'DELETE' }),
  close: (token: string, id: string) => request<SpaceDetail>(`/spaces/${encodeURIComponent(id)}/close`, { token, method: 'POST' }),
  list: (token: string) => request<SpaceSummary[]>('/spaces', { token }),
  get: (token: string, id: string) => request<SpaceDetail>(`/spaces/${encodeURIComponent(id)}`, { token }),
};

export { ApiError };
